import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FiShield, FiKey, FiCopy, FiCheck, FiX, FiAlertCircle } from 'react-icons/fi';
// import { twoFactorAuth } from '../../services/twoFactorAuth'; // Temporarily disabled
import { useAuth } from '../../hooks/useAuth';
import SafeIcon from '../../common/SafeIcon';

const TwoFactorSetup = () => {
  const { user } = useAuth();
  const [isEnabled, setIsEnabled] = useState(false);
  const [setupData, setSetupData] = useState(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [showBackupCodes, setShowBackupCodes] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [copiedCode, setCopiedCode] = useState('');

  useEffect(() => {
    // checkTwoFactorStatus();
  }, [user]);

  const checkTwoFactorStatus = async () => {
    if (!user) return;
    // const enabled = await twoFactorAuth.is2FAEnabled(user.id);
    // setIsEnabled(enabled);
  };

  const startSetup = async () => {
    try {
      setError('');
      // const data = await twoFactorAuth.generateSecret(user.email);
      // setSetupData(data);
      setError('2FA setup is temporarily disabled. Please check back later.');
    } catch (err) {
      setError('Failed to generate 2FA setup. Please try again.');
    }
  };

  const verifyAndEnable = async () => {
    if (!verificationCode || verificationCode.length !== 6) {
      setError('Please enter a valid 6-digit code');
      return;
    }

    setIsVerifying(true);
    setError('');

    try {
      // First verify the code works - temporarily disabled
      // const testTotp = new (await import('otpauth')).TOTP({
      //   secret: (await import('otpauth')).Secret.fromBase32(setupData.secret),
      //   algorithm: 'SHA1',
      //   digits: 6,
      //   period: 30
      // });

      // const delta = testTotp.validate({ token: verificationCode, window: 1 });
      
      // if (delta === null) {
      //   setError('Invalid verification code. Please try again.');
      //   setIsVerifying(false);
      //   return;
      // }

      // Enable 2FA - temporarily disabled
      // await twoFactorAuth.enable2FA(user.id, setupData.secret, setupData.backupCodes);
      setIsEnabled(true);
      setShowBackupCodes(true);
      setSuccess('Two-factor authentication has been successfully enabled!');
    } catch (err) {
      setError('Failed to enable 2FA. Please try again.');
    } finally {
      setIsVerifying(false);
    }
  };

  const disable2FA = async () => {
    if (!confirm('Are you sure you want to disable two-factor authentication? This will reduce your account security.')) {
      return;
    }

    try {
      // await twoFactorAuth.disable2FA(user.id);
      setIsEnabled(false);
      setSetupData(null);
      setSuccess('Two-factor authentication has been disabled.');
    } catch (err) {
      setError('Failed to disable 2FA. Please try again.');
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    setCopiedCode(label);
    setTimeout(() => setCopiedCode(''), 2000);
  };

  const regenerateBackupCodes = async () => {
    try {
      // const { backupCodes } = await twoFactorAuth.regenerateBackupCodes(user.id);
      const { backupCodes } = { backupCodes: [] };
      setSetupData(prev => ({ ...prev, backupCodes }));
      setSuccess('Backup codes have been regenerated.');
    } catch (err) {
      setError('Failed to regenerate backup codes.');
    }
  };

  if (isEnabled && !setupData) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
              <SafeIcon icon={FiShield} className="w-6 h-6 text-green-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold">Two-Factor Authentication</h3>
              <p className="text-sm text-green-600">Enabled and Active</p>
            </div>
          </div>
          <button
            onClick={disable2FA}
            className="px-4 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            Disable 2FA
          </button>
        </div>

        <button
          onClick={regenerateBackupCodes}
          className="px-4 py-2 bg-gray-100 text-gray-700 hover:bg-gray-200 rounded-lg transition-colors"
        >
          Regenerate Backup Codes
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-12 h-12 bg-primary-100 rounded-lg flex items-center justify-center">
          <SafeIcon icon={FiShield} className="w-6 h-6 text-primary-600" />
        </div>
        <div>
          <h3 className="text-lg font-semibold">Two-Factor Authentication Setup</h3>
          <p className="text-sm text-gray-600">Add an extra layer of security to your account</p>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          {error}
        </div>
      )}

      {success && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          {success}
        </div>
      )}

      {!setupData && !isEnabled && (
        <div>
          <p className="mb-4 text-gray-600">
            Two-factor authentication adds an extra layer of security by requiring a code from your phone in addition to your password.
          </p>
          <button
            onClick={startSetup}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Start Setup
          </button>
        </div>
      )}

      {setupData && !isEnabled && (
        <div className="space-y-6">
          <div>
            <h4 className="font-medium mb-2">Step 1: Scan QR Code</h4>
            <p className="text-sm text-gray-600 mb-3">
              Scan this QR code with your authenticator app (Google Authenticator, Authy, etc.)
            </p>
            <div className="flex justify-center mb-4">
              <img src={setupData.qrCodeDataUrl} alt="2FA QR Code" className="border-2 border-gray-300 rounded-lg" />
            </div>
            <div className="bg-gray-50 p-3 rounded-lg">
              <p className="text-xs text-gray-600 mb-2">Can't scan? Enter this code manually:</p>
              <div className="flex items-center space-x-2">
                <code className="text-xs font-mono bg-white px-2 py-1 rounded border flex-1">
                  {setupData.manualEntryKey}
                </code>
                <button
                  onClick={() => copyToClipboard(setupData.manualEntryKey, 'secret')}
                  className="p-1 hover:bg-gray-200 rounded transition-colors"
                >
                  <SafeIcon icon={copiedCode === 'secret' ? FiCheck : FiCopy} className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          <div>
            <h4 className="font-medium mb-2">Step 2: Verify Setup</h4>
            <p className="text-sm text-gray-600 mb-3">
              Enter the 6-digit code from your authenticator app to verify setup
            </p>
            <div className="flex space-x-2">
              <input
                type="text"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, '').slice(0, 6))}
                placeholder="000000"
                maxLength={6}
                className="px-3 py-2 border rounded-lg font-mono text-center text-lg tracking-wider flex-1 max-w-[150px]"
              />
              <button
                onClick={verifyAndEnable}
                disabled={isVerifying || verificationCode.length !== 6}
                className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isVerifying ? 'Verifying...' : 'Verify & Enable'}
              </button>
            </div>
          </div>
        </div>
      )}

      {showBackupCodes && setupData?.backupCodes && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-6 p-4 bg-yellow-50 border border-yellow-200 rounded-lg"
        >
          <div className="flex items-start space-x-2 mb-3">
            <SafeIcon icon={FiAlertCircle} className="w-5 h-5 text-yellow-600 mt-0.5" />
            <div className="flex-1">
              <h4 className="font-medium text-yellow-900">Save Your Backup Codes</h4>
              <p className="text-sm text-yellow-700 mt-1">
                Store these codes in a safe place. You can use them to access your account if you lose your phone.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2 mb-3">
            {setupData.backupCodes.map((code, index) => (
              <div key={index} className="flex items-center space-x-2 bg-white p-2 rounded">
                <code className="font-mono text-sm flex-1">{code}</code>
                <button
                  onClick={() => copyToClipboard(code, `code-${index}`)}
                  className="p-1 hover:bg-gray-100 rounded transition-colors"
                >
                  <SafeIcon icon={copiedCode === `code-${index}` ? FiCheck : FiCopy} className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
          <button
            onClick={() => copyToClipboard(setupData.backupCodes.join('\n'), 'all')}
            className="px-3 py-1.5 bg-yellow-600 text-white text-sm rounded hover:bg-yellow-700 transition-colors"
          >
            {copiedCode === 'all' ? 'Copied!' : 'Copy All Codes'}
          </button>
        </motion.div>
      )}
    </div>
  );
};

export default TwoFactorSetup;
